import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';
import { User } from '../types';

export interface InventoryStats {
  totalItems: number;
  totalQuantity: number;
  lowStock: number;
  outOfStock: number;
  costValue: number;
  sellValue: number;
  totalValue: number;
  categories: number;
}

const DEFAULT_STATS: InventoryStats = {
  totalItems: 0,
  totalQuantity: 0,
  lowStock: 0,
  outOfStock: 0,
  costValue: 0,
  sellValue: 0,
  totalValue: 0,
  categories: 0
};

const AUTO_REFRESH_INTERVAL = 30000;

export const useInventoryStats = (user: User | null, autoRefresh = true) => {
  const [stats, setStats] = useState<InventoryStats>(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const fetchStats = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) {
      setStats(DEFAULT_STATS);
      setLoading(false);
      return;
    }

    try {
      const { data, error: rpcError } = await supabase.rpc('get_inventory_stats_v2', {
        p_admin_id: adminId
      });

      if (rpcError) {
        throw rpcError;
      }

      if (data && Array.isArray(data) && data.length > 0) {
        const result = data[0];
        setStats({
          totalItems: result.total_items || 0,
          totalQuantity: result.total_quantity || 0,
          lowStock: result.low_stock || 0,
          outOfStock: result.out_of_stock || 0,
          costValue: parseFloat(result.cost_value) || 0,
          sellValue: parseFloat(result.sell_value) || 0,
          totalValue: parseFloat(result.total_value) || 0,
          categories: result.categories || 0
        });
        setError(null);
      } else if (data && !Array.isArray(data)) {
        setStats({
          totalItems: data.total_items || 0,
          totalQuantity: data.total_quantity || 0,
          lowStock: data.low_stock || 0,
          outOfStock: data.out_of_stock || 0,
          costValue: parseFloat(data.cost_value) || 0,
          sellValue: parseFloat(data.sell_value) || 0,
          totalValue: parseFloat(data.total_value) || 0,
          categories: data.categories || 0
        });
        setError(null);
      } else {
        throw new Error('No data returned from stats function');
      }
    } catch (err) {
      console.warn('RPC v2 failed, falling back to direct queries:', err);
      await fetchStatsFallback(adminId);
    } finally {
      setLoading(false);
    }
  }, [getAdminId]);

  const fetchStatsFallback = async (adminId: string) => {
    try {
      const [
        { count: totalCount },
        { count: lowStockCount },
        { count: outOfStockCount },
        { data: quantityData },
        { data: valueData },
        { data: categoryData }
      ] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId).gt('quantity', 0).lte('quantity', 10),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('admin_id', adminId).eq('quantity', 0),
        supabase.from('products').select('quantity').eq('admin_id', adminId),
        supabase.from('products').select('quantity, cost_price, sell_price').eq('admin_id', adminId),
        supabase.from('products').select('category').eq('admin_id', adminId)
      ]);

      const totalQuantity = quantityData?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      const costValue = valueData?.reduce((sum, item) => sum + ((item.quantity || 0) * (item.cost_price || 0)), 0) || 0;
      const sellValue = valueData?.reduce((sum, item) => sum + ((item.quantity || 0) * (item.sell_price || 0)), 0) || 0;
      const uniqueCategories = new Set(categoryData?.map(item => item.category).filter(Boolean));

      setStats({
        totalItems: totalCount || 0,
        totalQuantity,
        lowStock: lowStockCount || 0,
        outOfStock: outOfStockCount || 0,
        costValue,
        sellValue,
        totalValue: sellValue,
        categories: uniqueCategories.size
      });
      setError(null);
    } catch (fallbackErr) {
      console.error('Fallback stats fetch failed:', fallbackErr);
      setError('Failed to load inventory stats');
    }
  };

  const refreshStats = useCallback(async () => {
    setLoading(true);
    await fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (autoRefresh && user) {
      refreshIntervalRef.current = setInterval(fetchStats, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, user, fetchStats]);

  return {
    stats,
    loading,
    error,
    refreshStats
  };
};
